type TerminalExecutionTask = () => Promise<void>;

type QueueErrorHandler = (error: unknown, sessionId: string) => void;

export type TerminalExecutionQueue = {
  enqueue: (sessionId: string, task: TerminalExecutionTask) => Promise<void>;
  cancelChat: (chatId: string) => void;
};

function isSessionForChat({
  sessionId,
  chatId,
}: {
  sessionId: string;
  chatId: string;
}): boolean {
  return sessionId === chatId || sessionId.startsWith(`${chatId}:`);
}

export function createSessionTaskQueue({
  errorMessage,
  onError,
}: {
  errorMessage: string;
  onError?: QueueErrorHandler;
}): TerminalExecutionQueue {
  const handleError: QueueErrorHandler =
    onError ??
    ((error, sessionId) => {
      console.warn(`${errorMessage} ${sessionId}`, error);
    });
  const queues = new Map<string, Promise<void>>();
  const generations = new Map<string, number>();

  const bumpGeneration = (sessionId: string): number => {
    const generation = (generations.get(sessionId) ?? 0) + 1;

    generations.set(sessionId, generation);

    return generation;
  };

  return {
    cancelChat: (chatId) => {
      const sessionIds = new Set([
        chatId,
        ...generations.keys(),
        ...queues.keys(),
      ]);

      for (const sessionId of sessionIds) {
        if (isSessionForChat({ sessionId, chatId })) {
          bumpGeneration(sessionId);
        }
      }
    },
    enqueue: async (sessionId, task) => {
      const generation = generations.get(sessionId) ?? 0;
      const previous = queues.get(sessionId) ?? Promise.resolve();
      const run = async () => {
        try {
          await previous;
        } catch (error) {
          handleError(error, sessionId);
        }

        if ((generations.get(sessionId) ?? 0) !== generation) {
          return;
        }

        return task();
      };

      const result = run();
      const tail = result.then(
        () => undefined,
        (error) => {
          handleError(error, sessionId);
        },
      );

      queues.set(sessionId, tail);

      try {
        await result;
      } finally {
        if (queues.get(sessionId) === tail) {
          queues.delete(sessionId);
        }
      }
    },
  };
}

export function createTerminalExecutionQueue({
  onError = (error, sessionId) => {
    console.warn(
      `[TerminalExecution] Queued command failed for session ${sessionId}`,
      error,
    );
  },
}: {
  onError?: QueueErrorHandler;
} = {}): TerminalExecutionQueue {
  return createSessionTaskQueue({
    errorMessage: '[TerminalExecution] Queued command failed for session',
    onError,
  });
}

export function createTerminalResumeQueue({
  onError = (error, sessionId) => {
    console.warn(
      `[TerminalExecution] Queued terminal resume failed for session ${sessionId}`,
      error,
    );
  },
}: {
  onError?: QueueErrorHandler;
} = {}): TerminalExecutionQueue {
  return createSessionTaskQueue({
    errorMessage:
      '[TerminalExecution] Queued terminal resume failed for session',
    onError,
  });
}

export function scheduleTerminalExecution({
  sessionId,
  isBackground,
  execute,
  queue,
}: {
  sessionId: string;
  isBackground?: boolean;
  execute: TerminalExecutionTask;
  queue: Pick<TerminalExecutionQueue, 'enqueue'>;
}): Promise<void> {
  if (isBackground) {
    return execute();
  }

  return queue.enqueue(sessionId, execute);
}

export function scheduleTerminalResume({
  sessionId,
  send,
  queue,
}: {
  sessionId: string;
  send: TerminalExecutionTask;
  queue: Pick<TerminalExecutionQueue, 'enqueue'>;
}): Promise<void> {
  return queue.enqueue(sessionId, send);
}

let boundCancelForegroundQueues: ((chatId: string) => void) | null = null;

export function bindForegroundSessionQueues({
  queues,
}: {
  queues: TerminalExecutionQueue[];
}): void {
  boundCancelForegroundQueues = (chatId) => {
    for (const queue of queues) {
      queue.cancelChat(chatId);
    }
  };
}

export function cancelForegroundSessionQueues(chatId: string): void {
  boundCancelForegroundQueues?.(chatId);
}
